// Shared admin API client. Verticals call createAdminApi() with their own
// (per-vertical, e.g. "admin_tokens") auth store + translate fn, and get the
// full generic endpoint surface back. A vertical composes additional endpoints
// on top using the returned `fetchApi`/`getValidAccessToken` (e.g. gym /gym/*).
import { createApiTransport } from "../api";
import type { TokenData } from "../auth";
import type {
  DashboardStats,
  Service,
  Staff,
  Schedule,
  Unavailability,
  DayAvailabilityResponse,
  Booking,
  CalendarResponse,
  User,
  UserDetail,
  UserPointsResponse,
  Reschedule,
  EventLog,
  CompanySettings,
  LineDiagnosis,
  Assignee,
  Mailbox,
  MailboxInput,
  MailThreadListResponse,
  MailThreadResponse,
  MailUnreadCountResponse,
  ConversationListResponse,
  MessageThreadResponse,
  UnreadCountResponse,
  AdminBlogPost,
  BlogCategoryAdmin,
} from "./types";

export interface AdminApiDeps {
  getAccessToken: () => string | null;
  getTokens: () => TokenData | null;
  saveTokens: (tokens: TokenData) => void;
  clearTokens: () => void;
  isTokenExpired: (token: string) => boolean;
  parseJwtPayload: <T>(token: string) => T | null;
  getClientType: () => string;
  translate: (key: string, params?: Record<string, string>) => string;
}

export function createAdminApi(deps: AdminApiDeps) {
  const {
    getAccessToken,
    getTokens,
    saveTokens,
    clearTokens,
    isTokenExpired,
    parseJwtPayload,
    getClientType,
    translate,
  } = deps;

  const BASE_URL = "/api";

  const { fetchApi, fetchFile, refreshAccessToken, setOnSessionExpired } = createApiTransport({
    baseUrl: BASE_URL,
    getAccessToken,
    getTokens,
    saveTokens,
    getClientType,
    sessionExpiredMessage: () => translate("sessionExpired"),
    apiErrorMessage: (status) => translate("apiError", { status: String(status) }),
  });

  async function getValidAccessToken(): Promise<string | null> {
    const tokens = getTokens();
    if (!tokens) return null;

    if (isTokenExpired(tokens.access_token)) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) return null;
    }

    return getAccessToken();
  }

  async function ensureSessionOrRedirect(
    redirect: (path: string) => void,
  ): Promise<{ sub: string; email?: string; name?: string; role?: string } | null> {
    const tokens = getTokens();
    if (!tokens?.access_token) {
      clearTokens();
      redirect("/login");
      return null;
    }

    if (isTokenExpired(tokens.access_token)) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        clearTokens();
        redirect("/login");
        return null;
      }
    }

    const refreshedTokens = getTokens();
    if (!refreshedTokens?.access_token) {
      clearTokens();
      redirect("/login");
      return null;
    }

    try {
      const res = await fetch(`${BASE_URL}/admin/me`, {
        headers: { Authorization: `Bearer ${refreshedTokens.access_token}` },
      });
      if (!res.ok) {
        clearTokens();
        redirect(res.status === 403 ? `/login?error=${await refusalCode(res)}` : "/login");
        return null;
      }
      return (await res.json()) as Promise<{
        sub: string;
        email?: string;
        name?: string;
        role?: string;
        companyId?: number;
        timezone?: string;
      }>;
    } catch {
      clearTokens();
      redirect("/login");
      return null;
    }
  }

  /**
   * Decoded from the access token, so it carries only what auth_service put in the JWT.
   * It will NOT have companyId/timezone — those are app-domain values resolved server-side
   * by adminAuth. Use `api.me()` for those.
   */
  function getCurrentUser() {
    const tokens = getTokens();
    if (!tokens?.access_token) return null;
    return parseJwtPayload<{
      sub: string;
      email?: string;
      name?: string;
      role?: string;
    }>(tokens.access_token);
  }

  const api = {
    auth: {
      callback: (code: string, codeVerifier: string, redirectUri: string) =>
        fetchApi<{ access_token: string; refresh_token: string; expires_in: number }>(
          "/auth/callback",
          {
            method: "POST",
            body: JSON.stringify({ code, codeVerifier, redirectUri, clientType: getClientType() }),
          },
        ),
      refresh: (refreshToken: string) =>
        fetchApi<{ access_token: string; refresh_token: string; expires_in: number }>(
          "/auth/refresh",
          {
            method: "POST",
            body: JSON.stringify({ refreshToken, clientType: getClientType() }),
          },
        ),
      me: () => fetchApi<{ sub: string; email?: string; name?: string; role?: string }>("/auth/me"),
    },

    // The signed-in admin's own identity, including companyId/timezone. Available to
    // every role — unlike /admin/settings, which is admin-only and used to be where
    // pages bootstrapped companyId from, silently 403ing for staff.
    me: () =>
      fetchApi<{
        sub: string;
        email?: string;
        name?: string;
        role?: string;
        companyId?: number;
        timezone?: string;
      }>("/admin/me"),

    // A 30-second, single-use credential for opening an SSE stream. EventSource cannot
    // set an Authorization header, so the streams used to take the session JWT as
    // ?token= — a live credential in a URL. This call is header-authed like every other
    // one here; only its short-lived answer goes in the URL.
    //
    // Single-use, so this must be called again for EVERY connect, including reconnects.
    sseTicket: () =>
      fetchApi<{ ticket: string; expiresIn: number }>("/admin/sse-ticket", {
        method: "POST",
      }),

    dashboard: {
      stats: () => fetchApi<DashboardStats>("/admin/dashboard/stats"),
    },

    services: {
      list: () => fetchApi<Service[]>("/admin/services"),
      create: (data: Partial<Service>) =>
        fetchApi<Service>("/admin/services", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id: number, data: Partial<Service>) =>
        fetchApi<Service>(`/admin/services/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      delete: (id: number) =>
        fetchApi<{ message: string }>(`/admin/services/${id}`, {
          method: "DELETE",
        }),
      /** Full ordered list of this company's service ids; index becomes sortOrder. */
      reorder: (ids: number[]) =>
        fetchApi<{ message: string }>("/admin/services/reorder", {
          method: "PATCH",
          body: JSON.stringify({ ids }),
        }),
    },

    blog: {
      list: (params?: Record<string, string>) => {
        const qs = params ? "?" + new URLSearchParams(params).toString() : "";
        return fetchApi<{
          posts: AdminBlogPost[];
          total: number;
          page: number;
          limit: number;
        }>(`/admin/blog${qs}`);
      },
      get: (id: number) => fetchApi<AdminBlogPost>(`/admin/blog/${id}`),
      listCategories: () => fetchApi<BlogCategoryAdmin[]>("/admin/blog/categories"),
      // The admin tag chips count drafts too — unlike the public read — and
      // ?category= narrows the population to that section.
      listTags: (params?: { category?: number }) => {
        const qs = params?.category ? `?category=${params.category}` : "";
        return fetchApi<{ tag: string; count: number }[]>(`/admin/blog/tags${qs}`);
      },
      createCategory: (name: string) =>
        fetchApi<BlogCategoryAdmin>("/admin/blog/categories", {
          method: "POST",
          body: JSON.stringify({ name }),
        }),
      renameCategory: (id: number, name: string) =>
        fetchApi<BlogCategoryAdmin>(`/admin/blog/categories/${id}`, {
          method: "PATCH",
          body: JSON.stringify({ name }),
        }),
      deleteCategory: (id: number, moveTo?: number) =>
        fetchApi<{ message: string }>(
          `/admin/blog/categories/${id}${moveTo ? `?moveTo=${moveTo}` : ""}`,
          { method: "DELETE" },
        ),
      // The editor always sends the full row (the backend PATCH takes no
      // patch shape): publishedAt and tags ride along on every save.
      create: (data: {
        title: string;
        body: string;
        status: string;
        categoryId?: number | null;
        publishedAt?: string;
        tags?: string[];
      }) =>
        fetchApi<AdminBlogPost>("/admin/blog", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id: number, data: {
        title: string;
        body: string;
        status: string;
        categoryId?: number | null;
        publishedAt?: string;
        tags?: string[];
      }) =>
        fetchApi<AdminBlogPost>(`/admin/blog/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      remove: (id: number) =>
        fetchApi<{ message: string }>(`/admin/blog/${id}`, {
          method: "DELETE",
        }),
      // Multipart upload — a bare fetch like uploadAvatar above, so the browser
      // sets the multipart boundary itself (the shared transport is JSON-only).
      uploadImage: async (file: File) => {
        const token = await getValidAccessToken();
        const fd = new FormData();
        fd.append("image", file);
        const res = await fetch(`${BASE_URL}/admin/blog/images`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const err = new Error(
            body.message || translate("apiError", { status: String(res.status) }),
          );
          (err as Error & { status?: number; code?: string }).status = res.status;
          (err as Error & { status?: number; code?: string }).code = body.code;
          throw err;
        }
        return (await res.json()) as { url: string };
      },
    },

    staff: {
      list: () => fetchApi<Staff[]>("/admin/staff"),
      create: (data: { name: string; bio?: string; lineUserId?: string | null; notifyLine?: boolean; email?: string | null; notifyEmail?: boolean; serviceIds?: number[]; userId?: number | null }) =>
        fetchApi<Staff>("/admin/staff", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: { name?: string; bio?: string; lineUserId?: string | null; notifyLine?: boolean; email?: string | null; notifyEmail?: boolean; active?: boolean; serviceIds?: number[]; userId?: number | null },
      ) =>
        fetchApi<Staff>(`/admin/staff/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      delete: (id: number) =>
        fetchApi<{ message: string }>(`/admin/staff/${id}`, {
          method: "DELETE",
        }),
      // Persist a new display order (full ordered list of staff ids).
      reorder: (ids: number[]) =>
        fetchApi<{ message: string }>("/admin/staff/reorder", {
          method: "PATCH",
          body: JSON.stringify({ ids }),
        }),
      // Multipart upload — done with a bare fetch (not the JSON-only shared
      // transport) so the browser sets the multipart boundary itself.
      uploadAvatar: async (id: number, file: File) => {
        const token = await getValidAccessToken();
        const fd = new FormData();
        fd.append("photo", file);
        const res = await fetch(`${BASE_URL}/admin/staff/${id}/avatar`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const err = new Error(
            body.message || translate("apiError", { status: String(res.status) }),
          );
          // Same shape the shared transport throws, so a caller here can branch on the
          // status too — this was the one call in the app that opted out of that contract.
          (err as Error & { status?: number; code?: string }).status = res.status;
          (err as Error & { status?: number; code?: string }).code = body.code;
          throw err;
        }
        return res.json() as Promise<Staff>;
      },
      deleteAvatar: (id: number) =>
        fetchApi<Staff>(`/admin/staff/${id}/avatar`, {
          method: "DELETE",
        }),
      // `unavailabilities` is upcoming-only; elapsed blocks are permanent and grow
      // without bound, so they come from listPastUnavailability. `pastCount` is
      // just enough to label the button that opens them.
      getSchedule: (id: number) =>
        fetchApi<{
          schedules: Schedule[];
          unavailabilities: Unavailability[];
          pastCount: number;
        }>(`/admin/staff/${id}/schedule`),
      listPastUnavailability: (id: number, page: number, limit: number) =>
        fetchApi<{
          blocks: Unavailability[];
          total: number;
          page: number;
          limit: number;
        }>(`/admin/staff/${id}/unavailability/past?page=${page}&limit=${limit}`),
      dayAvailability: (id: number, date: string) =>
        fetchApi<DayAvailabilityResponse>(
          `/admin/staff/${id}/day-availability?date=${date}`,
        ),
      updateSchedule: (
        id: number,
        schedules: {
          dayOfWeek: number;
          startTime: string;
          endTime: string;
          breakStart: string | null;
          breakEnd: string | null;
        }[],
      ) =>
        fetchApi<Schedule[]>(`/admin/staff/${id}/schedule`, {
          method: "PUT",
          body: JSON.stringify({ schedules }),
        }),
      addUnavailability: (
        id: number,
        data: {
          date: string;
          startTime?: string;
          endTime?: string;
          reason?: string;
        },
      ) =>
        fetchApi<Unavailability>(
          `/admin/staff/${id}/unavailability`,
          { method: "POST", body: JSON.stringify(data) },
        ),
      deleteUnavailability: (staffId: number, blockId: number) =>
        fetchApi<{ message: string }>(
          `/admin/staff/${staffId}/unavailability/${blockId}`,
          { method: "DELETE" },
        ),
    },

    bookings: {
      list: (params?: Record<string, string>) => {
        const qs = params ? "?" + new URLSearchParams(params).toString() : "";
        return fetchApi<{
          bookings: Booking[];
          total: number;
          page: number;
          limit: number;
        }>(`/admin/bookings${qs}`);
      },
      calendar: (month: string) =>
        fetchApi<CalendarResponse>(
          `/admin/bookings/calendar?month=${month}`,
        ),
      updateStatus: (id: number, status: string) =>
        fetchApi<Booking>(`/admin/bookings/${id}/status`, {
          method: "PATCH",
          body: JSON.stringify({ status }),
        }),
      reschedule: (id: number, data: { newDate: string; newStartTime: string }) =>
        fetchApi<Booking>(`/admin/bookings/${id}/reschedule`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      create: (data: {
        serviceId: number;
        staffId: number;
        date: string;
        startTime: string;
        /** Omit for a "Note only" booking — the API then requires `notes`. */
        userId?: number;
        durationMin?: number;
        notes?: string;
      }) =>
        fetchApi<Booking>("/admin/bookings", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      delete: (id: number) =>
        fetchApi<{ message: string }>(`/admin/bookings/${id}`, {
          method: "DELETE",
        }),
    },

    users: {
      list: (params?: Record<string, string>) => {
        const qs = params ? "?" + new URLSearchParams(params).toString() : "";
        return fetchApi<{
          users: User[];
          total: number;
          page: number;
          limit: number;
        }>(`/admin/users${qs}`);
      },
      get: (id: number) =>
        fetchApi<UserDetail>(`/admin/users/${id}`),
      points: (id: number) =>
        fetchApi<UserPointsResponse>(`/admin/users/${id}/points`),
      create: (data: { name: string; phone: string; email: string }) =>
        fetchApi<User>("/admin/users", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (
        id: number,
        data: { name?: string; phone?: string; email?: string; status?: string },
      ) =>
        fetchApi<User>(`/admin/users/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      updateStatus: (id: number, status: string) =>
        fetchApi<User>(`/admin/users/${id}/status`, {
          method: "PATCH",
          body: JSON.stringify({ status }),
        }),
      resetPassword: (id: number, password: string) =>
        fetchApi<{ message: string }>(`/admin/users/${id}/password`, {
          method: "PUT",
          body: JSON.stringify({ password }),
        }),
      delete: (id: number) =>
        fetchApi<{ message: string }>(`/admin/users/${id}`, {
          method: "DELETE",
        }),
    },

    reschedules: {
      list: (params?: Record<string, string>) => {
        const qs = params ? "?" + new URLSearchParams(params).toString() : "";
        return fetchApi<{
          reschedules: Reschedule[];
          total: number;
          page: number;
          limit: number;
        }>(`/admin/reschedules${qs}`);
      },
      delete: (id: number) =>
        fetchApi<{ message: string }>(`/admin/reschedules/${id}`, {
          method: "DELETE",
        }),
    },

    logs: {
      list: (params?: Record<string, string>) => {
        const qs = params ? "?" + new URLSearchParams(params).toString() : "";
        return fetchApi<{
          logs: EventLog[];
          total: number;
          page: number;
          limit: number;
        }>(`/admin/logs${qs}`);
      },
    },

    settings: {
      get: () => fetchApi<CompanySettings>("/admin/settings"),
      update: (data: Partial<CompanySettings>) =>
        fetchApi<CompanySettings>("/admin/settings", {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      // Multipart upload — a bare fetch like uploadAvatar above, so the browser
      // sets the multipart boundary itself (the shared transport is JSON-only).
      // The bytes and the column pointer land in one request, which is why the
      // logo is not a field on `update`.
      uploadLogo: async (file: File) => {
        const token = await getValidAccessToken();
        const fd = new FormData();
        fd.append("logo", file);
        const res = await fetch(`${BASE_URL}/admin/settings/logo`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const err = new Error(
            body.message || translate("apiError", { status: String(res.status) }),
          );
          (err as Error & { status?: number; code?: string }).status = res.status;
          (err as Error & { status?: number; code?: string }).code = body.code;
          throw err;
        }
        return res.json() as Promise<CompanySettings>;
      },
      deleteLogo: () =>
        fetchApi<CompanySettings>("/admin/settings/logo", {
          method: "DELETE",
        }),
      triggerRichMenu: () =>
        fetchApi<{ message: string }>("/admin/settings/rich-menu", {
          method: "POST",
        }),
      diagnoseLine: () =>
        fetchApi<LineDiagnosis>("/admin/settings/line/diagnose"),
      // Separate from diagnoseLine on purpose: this one answers before any
      // credential is stored, which is when the LINE console needs the value.
      lineWebhookUrl: () =>
        fetchApi<{ webhookUrl: string }>("/admin/settings/line/webhook-url"),
      // The LIFF app's Endpoint URL, the one field on the LINE side that can be
      // wrong while every stored credential is right. Its own call for the same
      // reason lineWebhookUrl has one: neither needs a channel to exist.
      lineLiffEndpointUrl: () =>
        fetchApi<{ liffEndpointUrl: string }>("/admin/settings/line/liff-endpoint-url"),
    },

    messages: {
      list: (params?: { status?: string; assigned?: string; q?: string; page?: number; limit?: number }) => {
        const p: Record<string, string> = {};
        if (params?.status) p.status = params.status;
        if (params?.assigned) p.assigned = params.assigned;
        if (params?.q) p.q = params.q;
        if (params?.page) p.page = String(params.page);
        if (params?.limit) p.limit = String(params.limit);
        const qs = new URLSearchParams(p).toString();
        return fetchApi<ConversationListResponse>(`/admin/messages${qs ? `?${qs}` : ""}`);
      },
      get: (id: number) =>
        fetchApi<MessageThreadResponse>(`/admin/messages/${id}`),
      getByUser: (userId: number) =>
        fetchApi<MessageThreadResponse>(`/admin/users/${userId}/conversation`),
      reply: (id: number, text: string) =>
        fetchApi<{ message: { id: number } }>(`/admin/messages/${id}/reply`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        }),
      markRead: (id: number) =>
        fetchApi<{ success: boolean }>(`/admin/messages/${id}/read`, {
          method: "PATCH",
        }),
      setStatus: (id: number, status: string) =>
        fetchApi<{ success: boolean }>(`/admin/messages/${id}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }),
      assign: (id: number, staffAuthId: string | null) =>
        fetchApi<{ success: boolean }>(`/admin/messages/${id}/assign`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ staffAuthId }),
        }),
      delete: (id: number) =>
        fetchApi<{ success: boolean }>(`/admin/messages/${id}`, {
          method: "DELETE",
        }),
      unreadCount: () =>
        fetchApi<UnreadCountResponse>("/admin/messages/unread-count"),
      assignees: () =>
        fetchApi<{ assignees: Assignee[] }>("/admin/messages/assignees"),
      lineQuota: () =>
        fetchApi<{ type: string; total: number | null; used: number }>("/admin/messages/line-quota"),
    },

    mail: {
      list: (params?: { status?: string; q?: string; page?: number; limit?: number; mailboxId?: number }) => {
        const p: Record<string, string> = {};
        if (params?.status) p.status = params.status;
        if (params?.q) p.q = params.q;
        if (params?.page) p.page = String(params.page);
        if (params?.limit) p.limit = String(params.limit);
        if (params?.mailboxId) p.mailbox_id = String(params.mailboxId);
        const qs = new URLSearchParams(p).toString();
        return fetchApi<MailThreadListResponse>(`/admin/mail${qs ? `?${qs}` : ""}`);
      },
      get: (id: number) =>
        fetchApi<MailThreadResponse>(`/admin/mail/${id}`),
      reply: (id: number, text: string) =>
        fetchApi<{ success: boolean; email_id: number; message_id: string }>(`/admin/mail/${id}/reply`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        }),
      send: (data: { to: string; subject: string; text: string; mailboxId?: number }) =>
        fetchApi<{ success: boolean; thread_id: number; email_id: number; message_id: string }>(`/admin/mail/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }),
      markRead: (id: number) =>
        fetchApi<{ success: boolean }>(`/admin/mail/${id}/read`, { method: "PATCH" }),
      setStatus: (id: number, status: string) =>
        fetchApi<{ success: boolean }>(`/admin/mail/${id}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }),
      delete: (id: number) =>
        fetchApi<{ success: boolean }>(`/admin/mail/${id}`, {
          method: "DELETE",
        }),
      unreadCount: (mailboxId?: number) =>
        fetchApi<MailUnreadCountResponse>(
          `/admin/mail/unread-count${mailboxId ? `?mailbox_id=${mailboxId}` : ""}`,
        ),
      // Returns the bytes, not a URL. An <a href> download cannot set an Authorization
      // header, so the old URL form carried the session JWT as ?token= — into browser
      // history and every log on the path. Callers make an object URL from this instead.
      attachment: (id: number) => fetchFile(`/admin/mail/attachments/${id}`),
      mailboxes: () =>
        fetchApi<{ mailboxes: Mailbox[] }>("/admin/mailboxes"),
      createMailbox: (data: MailboxInput) =>
        fetchApi<{ success: boolean; id: number }>("/admin/mailboxes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }),
      updateMailbox: (id: number, data: MailboxInput) =>
        fetchApi<{ success: boolean }>(`/admin/mailboxes/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }),
      deleteMailbox: (id: number) =>
        fetchApi<{ success: boolean }>(`/admin/mailboxes/${id}`, { method: "DELETE" }),
    },

    availability: {
      dates: (params: { companyId: number; serviceId: number; staffId: number; month: string; durationMin?: number }) => {
        const p: Record<string, string> = {
          companyId: String(params.companyId),
          serviceId: String(params.serviceId),
          staffId: String(params.staffId),
          month: params.month,
        };
        if (params.durationMin) p.durationMin = String(params.durationMin);
        const qs = new URLSearchParams(p).toString();
        return fetchApi<{ date: string; hasSlots: boolean }[]>(`/availability/dates?${qs}`);
      },
      slots: (params: { companyId: number; serviceId: number; staffId: number; date: string; durationMin?: number }) => {
        const p: Record<string, string> = {
          companyId: String(params.companyId),
          serviceId: String(params.serviceId),
          staffId: String(params.staffId),
          date: params.date,
        };
        if (params.durationMin) p.durationMin = String(params.durationMin);
        const qs = new URLSearchParams(p).toString();
        return fetchApi<{ startTime: string; endTime: string; available: boolean }[]>(`/availability/slots?${qs}`);
      },
    },
  };

  return {
    api,
    fetchApi,
    getValidAccessToken,
    ensureSessionOrRedirect,
    getCurrentUser,
    setOnSessionExpired,
    BASE_URL,
  };
}

export type AdminApi = ReturnType<typeof createAdminApi>["api"];

/**
 * The reason a 403 was returned, as a query value for the sign-in screen.
 *
 * The API answers with a stable `code` (httpx AppError.WithCode) precisely so
 * clients need not match translated message strings. The two it can carry here
 * are different situations with different remedies: a missing role is worth
 * asking an administrator about, while a deactivated account cannot be fixed by
 * signing in again. Collapsing both into "access_denied", as this did, told a
 * suspended user to go and request access they already had.
 *
 * Falls back to the old value when the body is unreadable or carries no code,
 * so an older API keeps working.
 */
async function refusalCode(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { code?: string };
    if (body.code === "ACCOUNT_INACTIVE") return "account_inactive";
    if (body.code === "INSUFFICIENT_ROLE") return "access_denied";
  } catch {
    // A 403 with no JSON body is still a refusal; say the generic thing.
  }
  return "access_denied";
}

